import { StatusDot } from "../atoms/StatusDot/index.js";
import { relativeTime } from "./relative-time.js";
import type {
  NotificationItem,
  NotificationTone,
} from "./notifications.types.js";

const TONE_DOT: Record<NotificationTone, string> = {
  red: "bg-pb-red",
  amber: "bg-pb-amber",
  teal: "bg-pb-teal",
  cyan: "bg-pb-cyan",
};

/**
 * The render-side twin of `services/notification`'s own `HREF_PATTERN`
 * (`notification.schema.ts`) — same rule, independently enforced at the
 * boundary that actually puts the value in an anchor. `NotificationItem.href`
 * is a persisted string with no compile-time guarantee it went through that
 * schema (a future producer, a migration, or a schema change could bypass
 * it), so this is defense in depth, not a duplicate of the same check: a
 * value that reaches here unsafe renders as the plain (non-navigating)
 * button row instead of a clickable link. Exactly one leading `/`, nothing
 * that could turn a same-origin path into somewhere else — no `//` or `/\`
 * (protocol-relative), no whitespace, no control characters. A scheme such
 * as `javascript:` cannot match at all, because the value must start with `/`.
 */
// Mirrors HREF_PATTERN exactly.
const SAFE_HREF_PATTERN = /^\/(?![/\\])[^\s\x00-\x1f\x7f]*$/;

/**
 * EXPORTED (PB-1583) because a second notification surface — the shell's
 * full-page feed — now activates rows too, and two copies of a safety
 * predicate is exactly how one of them ends up laxer than the other.
 */
export function isSafeNotificationHref(
  href: string | undefined,
): href is string {
  return href !== undefined && SAFE_HREF_PATTERN.test(href);
}

/**
 * One row of the notifications feed.
 *
 * Extracted from `NotificationsPopover` so that file stays under this package's
 * 200-line soft cap, and because the row is three concerns at once — markup,
 * href SAFETY validation, and click handling whose branches differ by modifier
 * key. The DOM is unchanged by the move: same element choice (anchor when the
 * href is safe, plain button otherwise), same classes, same handlers, so nothing
 * about the accessibility tree or the existing tests shifts.
 *
 * `onDismiss` is the popover's own close. It is passed in rather than reached
 * for, because closing is the PARENT's business — the row should not know it
 * lives in a popover at all.
 */
export function NotificationRow({
  item,
  onMarkRead,
  onNavigate,
  onDismiss,
}: {
  readonly item: NotificationItem;
  readonly onMarkRead: (id: string) => void;
  /**
   * The ITEM travels with its href. Several notifications about one workspace
   * legitimately share an href, so a host that looked the row up by href alone
   * could pick a sibling and label the opened tab with the wrong title. A
   * caller that only wants the href still assigns cleanly — an extra parameter
   * is ignored.
   */
  readonly onNavigate?: (href: string, item: NotificationItem) => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  /* Unread is the full-page feed's info cyan edge and wash (PB-5441 d3), so
     the tray and the Notifications page show unread the same way; the dot
     beside the title stays. */
  const rowClassName = `w-full text-start flex items-start gap-3 border-s-[3px] px-3 py-2.5 hover:pb-overlay-paint transition-colors ${item.unread ? "border-s-pb-cyan bg-pb-cyan/5" : "border-s-transparent"}`;
  const rowContent = (
    <>
      <div className="pt-1.5">
        <span
          className={`block w-1.5 h-1.5 rounded-full ${TONE_DOT[item.tone]}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-pb-text truncate">
            {item.title}
          </div>
          {item.unread && <StatusDot tone="info" size="xs" />}
        </div>
        <div className="text-xs text-pb-text-secondary leading-snug mt-0.5">
          {item.description}
        </div>
        <div className="text-dense-1 text-pb-text-muted mt-1">
          {relativeTime(item.ts)}
        </div>
      </div>
    </>
  );
  const href = item.href;
  return isSafeNotificationHref(href) ? (
    <a
      href={href}
      onClick={(event) => {
        onMarkRead(item.id);
        const isUnmodifiedPrimaryClick =
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey;
        if (onNavigate && isUnmodifiedPrimaryClick) {
          event.preventDefault();
          /* Dismiss, exactly as "View all" does below. This is an
     SPA navigation, so nothing unmounts the popover on its
     own and the feed panel was left floating over the
     destination page.

     Only on THIS branch. A modified click (cmd/ctrl/shift)
     opens the target elsewhere and deliberately leaves this
     page as it was, so closing the panel the reader is
     still working in would be wrong; a full-page navigation
     (no `onNavigate`) unloads the document anyway. */
          onDismiss();
          onNavigate(href, item);
        }
      }}
      className={rowClassName}
    >
      {rowContent}
    </a>
  ) : (
    <button
      type="button"
      onClick={() => onMarkRead(item.id)}
      className={rowClassName}
    >
      {rowContent}
    </button>
  );
}
